import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { Link as RouterLink, useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

import List from '@material-ui/core/List';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';

import AddIcon from '@material-ui/icons/Add';
import CreditCardIcon from '@material-ui/icons/CreditCard';

import SavePaymentMethodDialog from 'app/components/accounting/payments/SavePaymentMethodDialog';
import EditPaymentMethodDialog from 'app/components/accounting/payments/EditPaymentMethodDialog';
import PaymentMethodItem from 'app/components/accounting/payments/PaymentMethodItem';
import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';

import InvoiceStatusNoHooks from 'app/utils/InvoiceStatusNoHooks';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    paymentMethods: {
        marginTop: theme.spacing(3),
    },
    table: {
        minWidth: '100%',
    },
    tableWrapper: {
        overflowX: 'auto',

        [theme.breakpoints.down('sm')]: {
            overflowX: 'inherit'
        }
    },
    tableRow: {
        cursor: 'pointer'
    },
    tableCell: {
        whiteSpace: "nowrap"
    },
    invoiceBackground: {
        backgroundColor: theme.palette.grey[100],
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginTop: '24px',
        marginBottom: '0px'
    },
    mobileButton: {
        width: '100%',
        marginTop: theme.spacing(1),
    },
    mobileItem: {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        paddingBottom: theme.spacing(3),
        marginTop: theme.spacing(3),

        '&:last-of-type': {
            borderBottom: '0px',
            paddingBottom: '0px',
        }
    },
    success: {
        color: theme.palette.success.main
    },
    primary: {
        color: theme.palette.primary.main
    },
    warning: {
        color: theme.palette.warning.main
    },
    error: {
        color: theme.palette.error.main
    }
}));

export default function Accounting(props) {

    const api = useApi();
    const history = useHistory();

    const { user, deleted } = props;

    const [ paymentMethods, setPaymentMethods ] = useState([]);
    const [ defaultMethod, setDefaultMethod ] = useState(null);
    const [ invoices, setInvoices ] = useState([]);

    const [ editingMethod, setEditingMethod ] = useState(null);
    const [ invoicePage, setInvoicePage ] = useState(0);

    const [ savePaymentMethodDialogOpen, setSavePaymentMethodDialogOpen ] = useState(false);
    const [ editPaymentMethodDialogOpen, setEditPaymentMethodDialogOpen ] = useState(false);

    const [ paymentMethodsLoading, setPaymentMethodsLoading ] = useState(false);
    const [ invoicesLoading, setInvoicesLoading ] = useState(false);

    const classes = useStyles();

    // Getters --------------------------------------

    const getPaymentMethods = () => {
        setPaymentMethodsLoading(true);

        api.get(`/admin/user/${user.id}/payment/method`)
            .then(res => {
                setPaymentMethods(res.methods);
                setDefaultMethod(res.default_payment_method)
            }).finally(() => setPaymentMethodsLoading(false));
    }

    const getInvoices = () => {
        setInvoicesLoading(true);

        api.get(`/admin/user/${user.id}/payment/invoice`)
            .then(res => setInvoices(res.invoices))
            .finally(() => setInvoicesLoading(false));
    }

    useEffect(() => {
        if (user) {
            getPaymentMethods();
            getInvoices();
        }
    }, [user]);

    // Handlers -------------------------
    const handleChangeInvoicePage = (event, newPage) => {
        setInvoicePage(newPage);
    }

    const goToInvoice = id => {
        history.push(`/admin/accounting/invoices/view/${id}`)
    }

    // Save Card Dialog --------------------------------------

    const openSavePaymentMethodDialog = () => {
        setSavePaymentMethodDialogOpen(true);
    }

    const closeSavePaymentMethodDialog = () => {
        setSavePaymentMethodDialogOpen(false);
    }

    const closeSavePaymentMethodDialogAndRefresh = () => {
        closeSavePaymentMethodDialog();
        getPaymentMethods();
    }

    // Edit Payment Method Dialog --------------------------------------

    const openEditPaymentMethodDialog = (method) => {
        setEditingMethod(method);
        setEditPaymentMethodDialogOpen(true);
    }

    const closeEditPaymentMethodDialog = () => {
        setEditPaymentMethodDialogOpen(false);
    }

    const closeEditPaymentMethodDialogAndRefresh = () => {
        closeEditPaymentMethodDialog();
        getPaymentMethods();
    }

    const editPaymentMethodDialogClosed = () => {
        setEditingMethod(null);
    }

    const emptyRows = 10 - invoices.slice((invoicePage * 10), (invoicePage * 10) + 10).length;

    return (
        <form
            noValidate
            autoComplete="off"
            >
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Payment Information
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Here you can view / edit a users saved payment methods and invoices.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={paymentMethodsLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Payment Methods
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={openSavePaymentMethodDialog}
                            startIcon={<CreditCardIcon />}
                            disabled={deleted}
                            >
                            Add Payment Method
                        </Button>
                    </Grid>
                </Grid>
                { paymentMethods.length ? (
                    <List className={classes.paymentMethods} disablePadding dense>
                        {paymentMethods.map(method => <PaymentMethodItem key={method.id} method={method} defaultMethod={defaultMethod} openEditPaymentMethodDialog={openEditPaymentMethodDialog} />)}
                    </List>
                ) : (
                    <Typography variant="body2" component="h5" color="textSecondary" gutterBottom>
                        User has no saved payment methods.
                    </Typography>
                )}
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={invoicesLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Invoices
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            component={RouterLink}
                            to={`/admin/accounting/invoices/0?user_id=${user ? user.id : 0}`}
                            startIcon={<AddIcon />}
                            disabled={deleted}
                            >
                            New Invoice
                        </Button>
                    </Grid>
                </Grid>
                { invoices.length ? (
                    <div className={classes.tableWrapper}>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableCell}>
                                        Status
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Invoice #
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Description
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Due On
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        Amount
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { invoices.slice((invoicePage * 10), (invoicePage * 10) + 10).map(invoice => {
                                    const status = InvoiceStatusNoHooks(invoice);
                                    return (
                                        <TableRow
                                            key={invoice.id}
                                            hover
                                            onClick={() => goToInvoice(invoice.id)}
                                            className={classes.tableRow}
                                            >
                                            <TableCell
                                                className={classNames(classes.tableCell, classes[status.class])}
                                                component="th"
                                                scope="row"
                                                >
                                                {status.label}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {invoice.invoice_number}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {invoice.description}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Typography
                                                    variant="subtitle2"
                                                    color={status.id === 'late' ? "error" : "textSecondary"}
                                                    >
                                                    {DateTime.fromISO(invoice.due_by).toLocaleString(DateTime.DATE_MED)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                ${invoice.amount_f}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                {(emptyRows > 0) &&
                                    <TableRow style={{ height: 54 * emptyRows }}>
                                        <TableCell colSpan={5} />
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={invoices.length}
                            rowsPerPage={10}
                            page={invoicePage}
                            onChangePage={handleChangeInvoicePage}
                            rowsPerPageOptions={[10]}
                        />
                    </div>
                ) : (
                    <Typography variant="body2" component="h5" color="textSecondary" gutterBottom>
                        User has no invoices.
                    </Typography>
                )}
            </Paper>
            {user &&
                <>
                    <SavePaymentMethodDialog
                        user={user}
                        open={savePaymentMethodDialogOpen}
                        onSave={closeSavePaymentMethodDialogAndRefresh}
                        onClose={closeSavePaymentMethodDialog}
                    />
                    <EditPaymentMethodDialog
                        open={editPaymentMethodDialogOpen}
                        onSave={closeEditPaymentMethodDialogAndRefresh}
                        onClose={closeEditPaymentMethodDialog}
                        onClosed={editPaymentMethodDialogClosed}
                        method={editingMethod}
                        urlPrefix={`/admin/user/${user.id}`}
                        defaultMethodId={defaultMethod}
                    />
                </>
            }
        </form>
    )
}
